import React from 'react';
import s from './auth.module.css';

// Projects a classified error into the three slots the form renders: a
// top-of-form banner, an error under the email field, and one under the
// password field. The form reads these directly rather than carrying a set of
// booleans that have to be kept consistent with each other.
export default function authErrorView(error, mode, switchToLogin, switchToReset) {
  if (!error) return { banner: null, emailError: undefined, passwordError: undefined };

  switch (error.kind) {
    case 'wrongCreds':
      return {
        banner: {
          title: "Email or password didn't match.",
          body: (
            <>
              Check your details — or{' '}
              <span
                className={s.bannerLink}
                role="button"
                tabIndex={0}
                onClick={switchToReset}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') switchToReset?.(); }}
              >
                reset your password
              </span>.
              {/* An account made with Google has no password unless one was
                  set later, and a wrong-password message is how its owner
                  finds out they used the other button (#567). */}
              {' '}If you signed up with Google, use Continue with Google.
            </>
          ),
        },
        // Only flag the password field when signing in: "wrong credentials"
        // has no meaning while registering.
        //
        // The wording must not name which half was wrong. Firebase collapses
        // "no such user" and "wrong password" into one code on purpose (see
        // classifyError), so saying "Wrong password" both claims knowledge we
        // do not have and hands an attacker the account-enumeration signal the
        // collapse exists to deny.
        passwordError: mode === 'login'
          ? 'Check your password, or reset it above.'
          : undefined,
      };

    case 'tooMany':
      return { banner: { title: 'Too many attempts.', body: 'Wait a few minutes, then try again.' } };

    case 'banner':
      return { banner: { title: "Couldn't sign you in.", body: error.text } };

    case 'emailTaken':
      return {
        emailError: (
          <>
            This email already has an account.{' '}
            <span className={s.inlineLink} role="button" tabIndex={0} onClick={switchToLogin}>
              Sign in instead
            </span>.
          </>
        ),
      };

    // ── Google (#567) ───────────────────────────────────────────────────
    case 'dismissed':
      // The teacher closed the Google window. Nothing went wrong.
      return {};

    case 'googleAccountExists':
      // Firebase keeps one account per address, and this one signs in with a
      // password. LoginPage fills in the address and switches to Sign in;
      // once the password is accepted, Google is linked to the same account.
      return {
        banner: {
          title: 'This email already has an account.',
          body: 'Sign in with your password below, and Google will be added to it so you can use either next time.',
        },
      };

    case 'googleOnly':
      // The account's password went when Google took it over (#591), so it
      // is not "didn't match": there is no password to match. No error under
      // the password box, for the same reason. The Google button is directly
      // under this banner on the form, which is what "right there" means.
      return {
        banner: {
          title: 'This account signs in with Google.',
          body: 'It no longer has a password. Use Continue with Google below.',
        },
      };

    case 'googleUnavailable':
      return {
        banner: {
          title: "Google sign-in isn't available yet.",
          body: 'Use your email address and a password instead.',
        },
      };

    case 'popupBlocked':
      return {
        banner: {
          title: 'Your browser blocked the Google window.',
          body: 'Allow pop-ups for this site, then try again.',
        },
      };

    case 'invalidEmail':
      return { emailError: 'Please enter a valid email address.' };

    case 'missingEmail':
      return { emailError: 'Enter your email address first.' };

    case 'weakPassword':
      return { passwordError: 'Password must be at least 6 characters.' };

    default:
      return {};
  }
}
